// FULL FILE — GroupClinical.js
import React, { useState, useEffect, isValidElement } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Heart, School, FolderKanban, Stethoscope, Hospital, GripVertical, Rainbow } from 'lucide-react';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';

// keep destinations under /dashboard when mounted there
const underDash = (to, pathname) => {
  if (to.startsWith('/dashboard')) return to;
  if (pathname.startsWith('/dashboard') && to.startsWith('/')) return `/dashboard${to}`;
  return to;
};

const DASHBOARD_NAME = 'clinical';
const baseSections = [
  { label: 'Geriatric Clinic', path: '/clinical/geriatric', icon: <Heart size={32} /> },
  { label: 'Adolescent Clinic', path: '/clinical/adolescent', icon: <School size={32} /> },
  { label: 'Medical Camps', path: '/clinical/camps', icon: <FolderKanban size={32} /> },
  { label: 'Occupational Health', path: '/clinical/occupational', icon: <Stethoscope size={32} /> },
  { label: 'RMNCH Services', path: '/clinical/rmnch', icon: <Hospital size={32} /> },
  { label: 'LGBTQIA+ Care', path: '/clinical/lgbtqia', icon: <Rainbow size={32} /> },
  // Quick report panel aggregating all of the above
  { label: 'Clinical Services Report', path: '/clinical/report', icon: <FolderKanban size={32} /> },
].map(s => ({ ...s, roles: ['faculty', 'intern', 'junior resident', 'senior resident', 'admin'] }));

function SortableCard({ section, pathname }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.path });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : 'auto', opacity: isDragging ? 0.7 : 1 };

  return (
    <div ref={setNodeRef} style={style}>
      <Link to={underDash(section.path, pathname)} className="no-underline">
        <Card className="rounded-2xl shadow-md hover:shadow-xl bg-white dark:bg-[#26272b] border border-gray-200 dark:border-[#363640] transition-colors duration-300 ease-in-out cursor-pointer">
          <CardContent className="flex flex-col items-center justify-center py-8 relative">
            <span {...attributes} {...listeners} className="absolute top-2 right-2 cursor-grab active:cursor-grabbing" title="Drag to reorder">
              <GripVertical size={20} className="text-blue-900 dark:text-[#7cc7ff] opacity-70" />
            </span>
            <div className="mb-3 text-blue-700 dark:text-[#7cc7ff] transition-colors">
              {isValidElement(section.icon) ? section.icon : null}
            </div>
            <Typography variant="h6" align="center" className="text-gray-800 dark:text-gray-100 transition-colors">
              {section.label}
            </Typography>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}

export default function GroupClinical() {
  const user = JSON.parse(localStorage.getItem('loggedInUser')) || {};
  const role = (user?.role || user?.userType || '').toLowerCase();
  const visibleSectionsInit = baseSections.filter(section => section.roles.map(r => r.toLowerCase()).includes(role));
  const [sections, setSections] = useState(visibleSectionsInit);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    async function fetchOrder() {
      try {
        const res = await axios.get(`/api/dashboard-config/${DASHBOARD_NAME}/${role}`);
        if (res.data.order && Array.isArray(res.data.order)) {
          const map = Object.fromEntries(visibleSectionsInit.map(sec => [sec.path, sec]));
          const ordered = res.data.order.map(path => map[path]).filter(Boolean);
          const extra = visibleSectionsInit.filter(sec => !res.data.order.includes(sec.path));
          setSections([...ordered, ...extra]);
        } else {
          setSections(visibleSectionsInit);
        }
      } catch {
        setSections(visibleSectionsInit);
      } finally { setLoading(false); }
    }
    fetchOrder();
    // eslint-disable-next-line
  }, [role]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(sec => sec.path === active.id);
    const newIndex = sections.findIndex(sec => sec.path === over.id);
    const newOrder = arrayMove(sections, oldIndex, newIndex);
    setSections(newOrder);
    try {
      await axios.post(`/api/dashboard-config/${DASHBOARD_NAME}/${role}`, { order: newOrder.map(sec => sec.path) });
    } catch {}
  };

  return (
    <div className="p-6 bg-white dark:bg-[#232326] rounded-xl shadow border border-gray-200 dark:border-[#363640] min-h-screen transition-colors">
      <Typography variant="h6" className="mb-4 text-blue-900 dark:text-[#7cc7ff] font-semibold text-lg">Clinical Services</Typography>
      {loading ? (
        <div className="text-gray-400 dark:text-gray-500 text-center mt-10">Loading...</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map(section => section.path)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {sections.map((section) => (
                <SortableCard key={section.path} section={section} pathname={location.pathname} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
